import { useEffect, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { Building2, Plane, BookOpen, MapPinned, Newspaper, Camera, Compass } from "lucide-react";
import api from "../lib/api";
import { geoSlug } from "./ExploreGuide";
import { fetchGuide } from "../lib/guide";
import { Spinner, Pill, fmtNum } from "../components/common";
import { pickAirportBg, CITY_SEED_PHOTOS } from "../lib/assets";
import SectionTabs from "../components/SectionTabs";
import Breadcrumbs from "../components/Breadcrumbs";
import GuideSections from "../components/GuideSections";
import CountryTopAirports from "../components/CountryTopAirports";
import NewsFeed from "../components/NewsFeed";
import PhotoGallery from "../components/PhotoGallery";
import DiscussionBoard from "../components/DiscussionBoard";
import EntityStoriesFeed from "../components/EntityStoriesFeed";
import EntityHero from "../components/EntityHero";
import EssenceCard from "../components/travel/EssenceCard";

const TABS = [
  { id: "guide", label: "Guide", icon: BookOpen },
  { id: "airports", label: "Airports", icon: Building2 },
  { id: "airlines", label: "Airlines", icon: Plane },
  { id: "states", label: "States & Regions", icon: MapPinned },
  { id: "news", label: "News", icon: Newspaper },
  { id: "photos", label: "Photos", icon: Camera },
];

export default function CountryDetail() {
  const { code } = useParams();
  const { hash } = useLocation();
  const [c, setC] = useState(null);
  const [err, setErr] = useState(false);
  const [guide, setGuide] = useState(null);
  const [guideLoading, setGuideLoading] = useState(true);
  const [regions, setRegions] = useState(null);
  const [tab, setTab] = useState(() => {
    const h = (hash || "").replace("#", "");
    return TABS.some((t) => t.id === h) ? h : "guide";
  });

  useEffect(() => {
    setC(null); setErr(false); setGuide(null); setGuideLoading(true); setRegions(null);
    api.get(`/countries/${code}`).then((r) => setC(r.data)).catch(() => setErr(true));
  }, [code]);

  useEffect(() => {
    if (!c) return;
    let alive = true;
    setGuideLoading(true);
    fetchGuide({ country: c.name })
      .then((g) => { if (alive) setGuide(g); })
      .finally(() => { if (alive) setGuideLoading(false); });
    // regions (states) + friendly names from SocialPulse subdivisions
    Promise.all([
      api.get("/airport-regions", { params: { country: c.id } }).then((r) => r.data.items).catch(() => []),
      api.get("/guide/subdivisions", { params: { country: c.id } }).then((r) => r.data.subdivisions || []).catch(() => []),
    ]).then(([items, subs]) => {
      if (!alive) return;
      const nameByCode = {};
      subs.forEach((s) => { if (s.code) nameByCode[s.code.toUpperCase()] = s.name; });
      setRegions(items.map((it) => ({ code: it.code, label: nameByCode[(it.code || "").toUpperCase()] || it.label })));
    });
    return () => { alive = false; };
  }, [c]);

  if (err) return <div className="max-w-3xl mx-auto px-5 py-24 text-center text-muted-foreground">Country not found.</div>;
  if (!c) return <Spinner full />;

  const HDR = pickAirportBg(c.name);
  const hasGuide = guide && guide.sections && Object.keys(guide.sections).length > 0;
  const tabs = TABS.filter((t) => t.id !== "guide" || guideLoading || hasGuide);
  const activeTab = tabs.some((t) => t.id === tab) ? tab : "airports";

  return (
    <div data-testid="country-detail">
      <EntityHero
        testid="country-hero"
        image={HDR}
        tone="violet"
        breadcrumbs={<Breadcrumbs className="text-white" items={[{ label: "Countries", to: "/countries" }, { label: c.name }]} />}
        tags={<><Pill tone="blue">{c.id}</Pill><Pill>{c.continent}</Pill></>}
        title={c.name}
        subtitle={<span className="font-mono text-sm">{fmtNum(c.airport_count)} airports registered</span>}
        actions={
          <Link to={`/explore/${geoSlug(c.name)}`} data-testid="explore-geo-link"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#4285F4] text-white text-sm font-700 hover:opacity-90 rounded-md">
            <Compass className="w-4 h-4" /> Explore {c.name} — full travel guide
          </Link>
        }
        quickFacts={[
          { label: "Continent",  value: c.continent },
          { label: "Airports",   value: fmtNum(c.airport_count || 0) },
          { label: "Country ID", value: c.id },
          { label: "Regions",    value: regions ? String(regions.length) : "—" },
        ]}
      />

      <EssenceCard kind="country" code={c.id} name={c.name} />

      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="pt-6"><SectionTabs tabs={tabs} active={activeTab} onChange={setTab} testid="country-tab" /></div>

        <div className="py-8 min-h-[280px]">
          {activeTab === "guide" && (
            <div data-testid="country-guide-panel">
              <h2 className="font-heading font-900 text-blue-600 text-2xl mb-5 flex items-center gap-2"><BookOpen className="w-6 h-6" /> About {c.name}</h2>
              {guideLoading ? <Spinner /> : hasGuide ? <GuideSections guide={guide} /> : <p className="text-sm text-muted-foreground">No detailed guide available for {c.name} yet.</p>}
            </div>
          )}

          {activeTab === "airports" && (
            <div data-testid="country-airports-panel">
              <CountryTopAirports countryName={c.name} countryCode={c.id} />
              <h2 className="font-heading font-900 text-blue-600 text-2xl mb-5 flex items-center gap-2"><Building2 className="w-6 h-6" /> Major airports</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {c.airports.map((a) => (
                  <Link key={a.id} to={`/airports/${a.iata_code || a.id}`} className="flex items-center justify-between border border-zinc-300 bg-white px-4 py-3 hover:border-primary transition-colors">
                    <span className="text-sm font-medium truncate">{a.name}</span>
                    <span className="font-mono text-primary text-sm shrink-0 ml-2">{a.iata_code || "—"}</span>
                  </Link>
                ))}
                {c.airports.length === 0 && <p className="text-sm text-muted-foreground">No major airports listed.</p>}
              </div>
            </div>
          )}

          {activeTab === "airlines" && (
            <div data-testid="country-airlines-panel">
              <h2 className="font-heading font-900 text-rose-600 text-2xl mb-5 flex items-center gap-2"><Plane className="w-6 h-6 -rotate-45" /> Airlines</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {c.airlines.map((a) => (
                  <Link key={a.id} to={`/airlines/${a.id}`} className="flex items-center justify-between border border-zinc-300 bg-white px-4 py-3 hover:border-primary transition-colors">
                    <span className="text-sm font-medium truncate">{a.name}</span>
                    <span className="font-mono text-primary text-sm shrink-0 ml-2">{a.iata || "—"}</span>
                  </Link>
                ))}
                {c.airlines.length === 0 && <p className="text-sm text-muted-foreground">No airlines listed.</p>}
              </div>
            </div>
          )}

          {activeTab === "states" && (
            <div data-testid="country-states-panel">
              <h2 className="font-heading font-900 text-emerald-600 text-2xl mb-5 flex items-center gap-2"><MapPinned className="w-6 h-6" /> States & Regions</h2>
              {regions === null ? <Spinner /> : regions.length ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {regions.map((r) => (
                    <Link key={r.code} to={`/regions/${r.code}`} data-testid={`country-region-${r.code}`}
                      className="group border border-zinc-300 bg-white p-4 flex items-center gap-2 hover:-translate-y-px hover:border-primary transition-[transform,border-color]">
                      <MapPinned className="w-4 h-4 text-primary shrink-0" />
                      <span className="font-600 text-sm truncate">{r.label}</span>
                    </Link>
                  ))}
                </div>
              ) : <p className="text-sm text-muted-foreground">No regions listed.</p>}
            </div>
          )}

          {activeTab === "news" && (
            <div data-testid="country-news-panel">
              <h2 className="font-heading font-900 text-cyan-600 text-2xl mb-5 flex items-center gap-2"><Newspaper className="w-6 h-6" /> {c.name} travel news</h2>
              <NewsFeed path={`/news?category=airport&q=${encodeURIComponent(c.name)}&limit=8&archive_as=country`} />
            </div>
          )}

          {activeTab === "photos" && (
            <div data-testid="country-photos-panel">
              <h2 className="font-heading font-900 text-orange-600 text-2xl mb-5 flex items-center gap-2"><Camera className="w-6 h-6" /> {c.name} photos</h2>
              <PhotoGallery entityType="country" entityId={c.id} seedPhotos={CITY_SEED_PHOTOS} />
            </div>
          )}
        </div>

        <SectionTabs tabs={tabs} active={activeTab} onChange={setTab} testid="country-tab-bottom" />

        <div className="py-12 space-y-10">
          <EntityStoriesFeed entityType="country" entityId={c.id} entityName={c.name} />
          <DiscussionBoard entityType="country" entityId={c.id} title={`${c.name} discussion board`} />
        </div>
      </div>
    </div>
  );
}
